import PageShell from "@/components/PageShell";
import PageHero from "@/components/PageHero";
import Layer1 from "@/components/Layer1";
import { Link } from "react-router-dom";
import { ArrowRight, Code2 } from "lucide-react";

const FUNCTIONS = [
    "Node Registration", "Validator Staking", "Gas Fees", "Compute Settlement",
    "Storage Settlement", "GPU Workload Settlement", "Proof of Storage", "On-Chain DNS",
    "Developer Revenue Registry", "Governance",
];

const CONTRACTS = [
    { name: "StorageCommitment.sol", body: "Commit shards, verify Proof of Storage, settle node rewards." },
    { name: "ComputeOrchestrator.sol", body: "Match workloads with nodes, escrow $DCX, finalize jobs." },
    { name: "AIInference.sol", body: "Register models, route inference, meter usage on-chain." },
    { name: "DNSResolver.sol", body: "On-chain naming and resolution records." },
    { name: "RevenueRegistry.sol", body: "Protocol-enforced developer revenue share distribution." },
];

export default function Layer1Page() {
    return (
        <PageShell>
            <PageHero
                eyebrow="DeCloudX Layer-1"
                title="A Layer-1 Built for Infrastructure,"
                accent="Not Speculation."
                sub="DeCloudX Layer-1 is an EVM-compatible chain purpose-built to coordinate decentralized cloud infrastructure. It verifies nodes, records workloads, settles payments, powers DNS, anchors storage proofs, and distributes rewards."
            />

            <Layer1 />

            <section className="relative py-16 md:py-24 border-t border-line">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="overline mb-3">Protocol Functions</div>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-ink max-w-3xl">Every infrastructure event happens on-chain.</h2>
                    <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-3">
                        {FUNCTIONS.map((f, i) => (
                            <div key={i} className="surface p-4 rounded-xl text-center" data-testid={`fn-${i}`}>
                                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-faint-ink mb-1">F{String(i+1).padStart(2,"0")}</div>
                                <div className="text-xs text-ink font-medium">{f}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section id="contracts" className="relative py-16 md:py-24 border-t border-line">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="overline mb-3">Smart Contracts</div>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-ink max-w-3xl">Purpose-built precompiles and core contracts.</h2>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {CONTRACTS.map((c, i) => (
                            <div key={i} className="surface-strong p-6 rounded-2xl" data-testid={`contract-${i}`}>
                                <div className="flex items-center gap-2 mb-3">
                                    <Code2 size={16} className="text-brand-violet" strokeWidth={1.5} />
                                    <span className="font-mono text-xs text-brand-violet">{c.name}</span>
                                </div>
                                <p className="text-sm text-muted-ink leading-relaxed">{c.body}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-12">
                        <Link to="/developers" className="btn-primary">Explore DeCloudX Layer-1 <ArrowRight size={15} strokeWidth={2} /></Link>
                    </div>
                </div>
            </section>
        </PageShell>
    );
}
